import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, ExternalLink, Flame, Youtube } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

// Brand/commercial & neutral videos first; the horror compilation sits last.
const CREATOR_VIDEOS = [
  {
    channel: "MagnatesMedia",
    url: "https://www.youtube.com/@MagnatesMedia",
    videoId: "51fWzJ-A5w4",
    title: "The Secret Chinese Company That Owns Everything",
    duration: "1:18:46",
  },
  {
    channel: "Dream Toon",
    url: "https://www.youtube.com/@DreamToonOfficial",
    videoId: "0TPanJOdTeQ",
    title: "चतुर बुढ़िया | Chatur Budhiya | Hindi Kahaniya | Animated Stories",
    duration: "26:39",
  },
  {
    channel: "Revenge Reborn",
    url: "https://www.youtube.com/@realrevengereborn",
    videoId: "le29mUSrz8s",
    title: "She Found an Ultrasound the Night Before Her Wedding—Then a Mafia Boss Changed Everything",
    duration: "2:21:50",
  },
  {
    channel: "Animospirit",
    url: "https://www.youtube.com/@Animospirit000",
    videoId: "6EY8eHwThaE",
    title: "8 True Terrifying Sleepover Horror Story | Real Scary Night Experience",
    duration: "1:07:46",
  },
  {
    channel: "SSG Animation",
    url: "https://www.youtube.com/@SSGAnimations",
    videoId: "OOKwliuYMWI",
    title: "22 TRUE Horror Stories Animated Compilation",
    duration: "3:22:39",
  },
];

const BRANDS = ["Coca-Cola", "Ola", "Uber", "Zomato", "Swiggy", "Bombay Shaving Co.", "Haldiram's"];

const thumb = (id, fb) => `https://i.ytimg.com/vi/${id}/${fb ? "hqdefault" : "maxresdefault"}.jpg`;

// Fewer than 4 items → static centred row (no marquee animation).
const Row = ({ items, render }) =>
  items.length < 4 ? (
    <div className="flex flex-wrap justify-center gap-4">{items.map((item, i) => render(item, i))}</div>
  ) : (
    <div className="relative overflow-hidden marquee-hover">
      <div className="animate-marquee w-max flex gap-4 pr-4">
        {items.map((item, i) => render(item, i))}
        {items.map((item, i) => render(item, `${i}-loop`))}
      </div>
    </div>
  );

const BrandChip = ({ name }) => (
  <div className="shrink-0 rounded-full border border-gray-100 bg-white shadow-soft px-8 py-4 font-display font-extrabold text-gray-900 text-xl whitespace-nowrap">
    {name}
  </div>
);

const CreatorChip = ({ c }) => (
  <a
    href={c.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`creator-partner-link-${c.channel.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="card-hover shrink-0 w-64 rounded-2xl border border-gray-100 bg-white shadow-soft px-6 py-5 flex items-center justify-between gap-4"
  >
    <div>
      <div className="font-display font-bold text-gray-900 leading-tight">{c.channel}</div>
      <div className="text-gray-400 text-xs mt-1">Partner Creator</div>
    </div>
    <Youtube className="w-4 h-4 text-crimson" />
  </a>
);

export default function Partners() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const active = CREATOR_VIDEOS[idx];

  useEffect(() => {
    if (paused || playing) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % CREATOR_VIDEOS.length), 6000);
    return () => clearInterval(t);
  }, [paused, playing]);

  const pick = (i) => {
    setIdx(((i % CREATOR_VIDEOS.length) + CREATOR_VIDEOS.length) % CREATOR_VIDEOS.length);
    setPlaying(false);
  };

  return (
    <section id="work" className="py-24 md:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <SectionTag className="justify-center">Our Partners</SectionTag>
          <h2 className="font-display font-extrabold text-gray-900 text-3xl md:text-5xl tracking-tight mt-4">
            Brands &amp; creators <span className="text-crimson">we work with.</span>
          </h2>
        </div>

        <div
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="partner-video-slider"
        >
          <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-[#0E0E12] shadow-[0_40px_100px_rgba(10,10,12,0.25)]">
            <AnimatePresence mode="wait">
              {playing ? (
                <motion.div
                  key={`play-${active.videoId}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative aspect-video bg-black"
                  data-testid="partner-video-embed"
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${active.videoId}?autoplay=1&rel=0`}
                    title={active.title}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </motion.div>
              ) : (
                <motion.div
                  key={`thumb-${active.videoId}`}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="relative aspect-video"
                >
                  <img src={thumb(active.videoId)} alt={`${active.channel} — ${active.title}`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <button
                    type="button"
                    data-testid="partner-play-button"
                    onClick={() => setPlaying(true)}
                    aria-label={`Play ${active.title}`}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <span className="w-20 h-20 rounded-full bg-crimson flex items-center justify-center shadow-[0_0_50px_rgba(255,42,59,0.55)] transition-transform duration-300 hover:scale-110">
                      <Play className="w-8 h-8 text-white fill-current" />
                    </span>
                  </button>
                  <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/70 uppercase">
                        <Flame className="w-3 h-3 text-crimson" /> Made by Vertex · {active.duration}
                      </div>
                      <h3 className="font-display font-bold text-white text-lg md:text-2xl mt-1.5 leading-snug truncate">
                        {active.title}
                      </h3>
                    </div>
                    <a
                      href={`https://www.youtube.com/watch?v=${active.videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="partner-watch-on-youtube-button"
                      aria-label={`Watch ${active.title} on YouTube`}
                      className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-gray-900 text-xs font-bold hover:bg-crimson hover:text-white transition-colors duration-300"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> YouTube
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            data-testid="partner-prev-button"
            onClick={() => pick(idx - 1)}
            aria-label="Previous video"
            className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-soft flex items-center justify-center text-gray-700 hover:text-crimson hover:border-crimson transition-colors duration-300 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="partner-next-button"
            onClick={() => pick(idx + 1)}
            aria-label="Next video"
            className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-soft flex items-center justify-center text-gray-700 hover:text-crimson hover:border-crimson transition-colors duration-300 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="flex items-center justify-center gap-2 mt-5">
            {CREATOR_VIDEOS.map((v, i) => (
              <button
                key={v.videoId}
                type="button"
                data-testid={`partner-slide-dot-${i}`}
                onClick={() => pick(i)}
                aria-label={`Go to ${v.channel} video`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-crimson" : "w-2 bg-gray-300 hover:bg-gray-400"}`}
              />
            ))}
          </div>
        </div>

        <div className="mt-16 space-y-8">
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] text-gray-400 uppercase mb-4">Partner Companies &amp; Brands</div>
            <Row items={BRANDS} render={(b, i) => <BrandChip key={`${b}-${i}`} name={b} />} />
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] text-gray-400 uppercase mb-4">Partner Creators</div>
            <Row items={CREATOR_VIDEOS} render={(c, i) => <CreatorChip key={`${c.channel}-${i}`} c={c} />} />
          </div>
          <p className="text-center text-sm text-gray-400">
            …plus partner agencies and shop owners across India.
          </p>
        </div>
      </div>
    </section>
  );
}
